import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import { useState } from "react";
import AsyncSelectScroll from "../../../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../components/CustomSelect";
import Input from "../../../../../../../components/Input";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import ProviderSelectScroll from "../../../../../../../components/ProviderSelectScroll";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { usePatientVaccineCreate } from "../../../../../../../queries/vaccines/use-patient-vaccine-create";
import { usePatientVaccineUpdate } from "../../../../../../../queries/vaccines/use-patient-vaccine-update";
import { usePublicVaccinesList } from "../../../../../../../queries/vaccines/use-public-vaccines-list";
import { PatientVaccine } from "../../../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { UNIT_OPTIONS } from "../../../../scheduling/appointment/encounter/prescription/PrescriptionFields";
import { VACCINE_SITE_LIST } from "../clinical-details/components/ClinicalLayout-constant";
import { ROUTE_LIST, VACCINE_VALIDATION } from "./vaccine-constant";
import NumberInput from "../../../../../../../components/NumberInput";

interface UpsertVaccineDrawerProps {
  disclosureHost: DisclosureHost;
  administeredBy?: {
    value: string;
    label: string;
  };
}

const UpsertVaccineDrawer = ({
  disclosureHost,
  administeredBy,
}: UpsertVaccineDrawerProps) => {
  // const encounterPatientId = useSearchParams()[0].get("patientid");
  // const appointmentId = useSearchParams()[0].get("appointmentid");
  // note : we are using useQueryParams instead of useSearchParams because we cannot access the search params in the drawer
  // const encounterPatientId = useQueryParams("patientid");

  const { patientId } = useParams();
  // const patientData: any = useOutletContext();
  const queryClient = useQueryClient();
  const { mutateAsync: createVaccine } = usePatientVaccineCreate();
  const { mutateAsync: updateVaccine } = usePatientVaccineUpdate();

  const showSnackbar = useSnackbar();
  const appointmentid = useQueryParams("appointmentid");

  const prefillData = disclosureHost?.data;

  const formik = useFormik<PatientVaccine>({
    initialValues: prefillData
      ? {
          ...prefillData,
          ...initialValues,
          administeredBy: administeredBy ?? prefillData?.administeredBy,
        }
      : {
          ...initialValues,
          vaccine: undefined,
          administeredBy: administeredBy ?? undefined,
        },
    validationSchema: VACCINE_VALIDATION,
    onSubmit: handleCreateVaccine,
    enableReinitialize: true,
  });

  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: vaccines,
    isLoading: isLoadingVaccine,
    isRefetching: isRefetchingVaccine,
  } = usePublicVaccinesList({
    pagination,
    searchString: searchKey,
    gridSorting: [],
    enabled: disclosureHost?.opened,
  });

  const transformedVaccines = vaccines?.data?.content?.map((it) => ({
    value: it?.uuid,
    label: it?.name,
  }));

  async function handleCreateVaccine(values: any) {
    const selectedPatient = {
      [patientId as string]: "",
    };

    let administeredByPayload = undefined;

    administeredByPayload = {
      [values?.administeredBy.value]: values?.administeredBy.label,
    };

    let vaccine = undefined;

    vaccine = {
      uuid: values?.vaccine.value,
      name: values?.vaccine.label,
    };

    const payload = {
      patientVaccine: {
        ...values,
        vaccine,
        patient: selectedPatient,
        administeredBy: administeredByPayload,
        appointmentId: appointmentid ?? undefined,
      },
      xTENANTID: getXtenantId(),
    };

    if (prefillData) showToastAndResetForm(updateVaccine(payload));
    else showToastAndResetForm(createVaccine(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-vaccine-list"],
        });

        handleClose();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function handleClose() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle={prefillData ? "Edit Vaccine" : "Add Vaccine"}
      dialogAction={
        <StyledButtonV2
          label="Save"
          variant="contained"
          onClick={formik.submitForm}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "40vw",
          height: "45vh",
        },
      }}
    >
      <Box pt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AsyncSelectScroll
              required
              name="vaccine"
              handleBlur={formik.handleBlur}
              value={formik.values?.vaccine as any}
              onSearch={(v) => {
                setSearchKey(v);
                setPagination((p) => ({ ...p, page: 0 }));
              }}
              pagination={pagination}
              setPagination={setPagination}
              totalElements={vaccines?.data?.page?.totalElements}
              onChange={(v) => formik.setFieldValue("vaccine", v)}
              loading={isLoadingVaccine || isRefetchingVaccine}
              label="Vaccine Name"
              placeholder="Select Or Search Vaccine"
              options={transformedVaccines}
              error={
                (formik.touched?.vaccine as any) &&
                (formik.errors?.vaccine as any)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <CustomDatePicker
              label="Administer Date"
              placeholder="Choose Date"
              value={
                formik.values?.administerDate
                  ? new Date(formik.values.administerDate)
                  : undefined
              }
              onChange={(val) =>
                formik.setFieldValue("administerDate", val?.toISOString())
              }
            />
          </Grid>
          <Grid item xs={6}>
            <ProviderSelectScroll
              required
              name="administeredBy"
              handleBlur={formik.handleBlur}
              value={formik.values?.administeredBy as any}
              onChange={(v) => formik.setFieldValue("administeredBy", v)}
              label="Administered By"
              prefillListOption={formik.values?.administeredBy as any}
              error={
                (formik.touched?.administeredBy as any) &&
                (formik.errors?.administeredBy as any)
              }
            />
          </Grid>
          <Grid item xs={3}>
            <CustomDatePicker
              label="Expiry Date"
              placeholder="Choose Date"
              value={
                formik.values?.expiryDate
                  ? new Date(formik.values.expiryDate)
                  : undefined
              }
              onChange={(val) =>
                formik.setFieldValue("expiryDate", val?.toISOString())
              }
            />
          </Grid>
          <Grid item xs={3}>
            <NumberInput
              label="Amount"
              min={0}
              textFieldProps={{
                placeholder: "Enter Amount",
                name: "dose",
                value: formik.values.dose,
                onChange: (e) =>
                  formik.setFieldValue("dose", e.target.value?.toString()),
                onBlur: formik.handleBlur,
              }}
              // error={touched.cpt_code && errors.cpt_code}
            />
          </Grid>
          <Grid item xs={3}>
            <CustomSelect
              label="Unit"
              name="units"
              placeholder="Unit"
              value={formik.values.units}
              options={UNIT_OPTIONS}
              onChange={(v) => formik.setFieldValue("units", v)}
            />
          </Grid>
          <Grid item xs={3}>
            <CustomSelect
              label="Route"
              name="route"
              placeholder="Route"
              value={formik.values.route}
              options={ROUTE_LIST}
              onChange={(v) => formik.setFieldValue("route", v)}
            />
          </Grid>
          <Grid item xs={3}>
            <CustomSelect
              label="Site"
              name="site"
              placeholder="Site"
              value={formik.values.site}
              options={VACCINE_SITE_LIST}
              onChange={(v) => formik.setFieldValue("site", v)}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              label="Manufacturer"
              textFieldProps={{
                placeholder: "Enter Manufacturer",
                name: "manufacturer",
                value: formik.values.manufacturer,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              // error={touched.cpt_code && errors.cpt_code}
            />
          </Grid>
          <Grid item xs={3}>
            <NumberInput
              label="Lot"
              textFieldProps={{
                placeholder: "Enter Lot",
                name: "lot",
                value: formik.values.lot,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              // error={touched.cpt_code && errors.cpt_code}
            />
          </Grid>
        </Grid>
      </Box>
    </StyledFormModal>
  );
};

export default UpsertVaccineDrawer;

const initialValues = {
  administerDate: new Date().toISOString(),
};
